import { useState } from "react";
import { Helmet } from "react-helmet-async";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionSuccess(false);
    setSubmissionError("");

    try {
      const response = await fetch(
        process.env.REACT_APP_CONTACT_API || "http://localhost:8000/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to send message.");
      }

      setIsSubmitting(false);
      setSubmissionSuccess(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      setIsSubmitting(false);
      setSubmissionError(error.message || "Failed to send message.");
    }
  };

  return (
    <main className="px-6 py-28 text-slate-200 md:px-12 lg:px-24">
      <Helmet>
        <title>Contact | Gabriel E.</title>
      </Helmet>

      <section className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <div>
          <p className="mb-4 text-sm font-medium uppercase tracking-[0.35em] text-cyan-400">
            Contact
          </p>

          <h1 className="text-4xl font-bold tracking-tight text-slate-100 md:text-6xl">
            Let&apos;s build something.
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-8 text-slate-400">
            Have a role, project, collaboration, or backend/full-stack
            engineering opportunity in mind? Send me a message and I&apos;ll get
            back to you.
          </p>

          <div className="mt-10 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-2xl shadow-slate-950/40">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-400">
              Focus Areas
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              {[
                "FastAPI",
                "PostgreSQL",
                "React",
                "Backend Systems",
                "Automation",
                "Cloud",
              ].map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-slate-700 bg-slate-950/50 px-4 py-2 text-sm text-slate-400"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-2xl shadow-slate-950/40 md:p-8"
        >
          <div className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-slate-300"
              >
                Full Name
              </label>

              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your name"
                className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950/70 px-4 py-3 text-slate-200 outline-none transition placeholder:text-slate-600 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-slate-300"
              >
                Email Address
              </label>

              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950/70 px-4 py-3 text-slate-200 outline-none transition placeholder:text-slate-600 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-semibold text-slate-300"
              >
                Message
              </label>

              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                placeholder="Tell me what you are working on..."
                className="mt-3 w-full resize-none rounded-lg border border-slate-700 bg-slate-950/70 px-4 py-3 text-slate-200 outline-none transition placeholder:text-slate-600 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
              />
            </div>

            {submissionSuccess && (
              <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-300">
                Thank you. Your message has been sent successfully.
              </div>
            )}

            {submissionError && (
              <div className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300">
                {submissionError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full rounded border px-6 py-3 text-sm font-medium transition ${
                isSubmitting
                  ? "cursor-not-allowed border-slate-700 bg-slate-800 text-slate-500"
                  : "border-cyan-400 text-cyan-300 hover:bg-cyan-400/10 hover:text-cyan-200"
              }`}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default ContactPage;
